const API_BASE = "/api";

export interface ClanMember {
  name: string;
  score: number;
  online: boolean;
}

export type ClanMode = "public" | "request" | "private";

export interface ClanDetail {
  id: number;
  name: string;
  leader: string;
  created: number;
  mode: ClanMode;
  members: ClanMember[];
  /** Pending join requests — present (leader-only) for request-mode clans. */
  requests?: string[];
  isLeader: boolean;
}

export interface ClanLeaderboardEntry {
  id: number;
  name: string;
  mode: ClanMode;
  members: number;
  score: number;
  rank: number;
}

type Result = { ok: boolean; error?: string };

/**
 * Thin API client + cache for clans. The compact "my clan" summary arrives via
 * the friends heartbeat (see Friends.myClan()); this system owns the detailed
 * member roster and the clan leaderboard, fetched only while the ClanScene is
 * open. Mutations return a fresh-fetch trigger to the caller.
 */
export class Clans {
  private detail: ClanDetail | null = null;
  private board: ClanLeaderboardEntry[] = [];

  clanDetail(): ClanDetail | null {
    return this.detail;
  }
  leaderboardEntries(): ClanLeaderboardEntry[] {
    return this.board.slice();
  }

  clear(): void {
    this.detail = null;
    this.board = [];
  }

  async fetchDetail(name: string, token: string, id: number): Promise<void> {
    try {
      const res = await fetch(
        `${API_BASE}/clans/get?name=${encodeURIComponent(name)}&id=${id}`,
        { headers: { authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        this.detail = null;
        return;
      }
      this.detail = (await res.json()) as ClanDetail;
    } catch {
      // keep cache
    }
  }

  async fetchLeaderboard(name: string, token: string): Promise<void> {
    try {
      const res = await fetch(`${API_BASE}/clans?name=${encodeURIComponent(name)}`, {
        headers: { authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = (await res.json()) as { clans?: ClanLeaderboardEntry[] };
      this.board = Array.isArray(data.clans) ? data.clans : [];
    } catch {
      // keep cache
    }
  }

  create(name: string, token: string, clanName: string, mode: ClanMode): Promise<Result> {
    return this.post("/clans/create", token, { name, clanName, mode });
  }
  join(name: string, token: string, id: number): Promise<Result> {
    return this.post("/clans/join", token, { name, id });
  }
  requestJoin(name: string, token: string, id: number): Promise<Result> {
    return this.post("/clans/request", token, { name, id });
  }
  approve(name: string, token: string, target: string): Promise<Result> {
    return this.post("/clans/approve", token, { name, target });
  }
  deny(name: string, token: string, target: string): Promise<Result> {
    return this.post("/clans/deny", token, { name, target });
  }
  setMode(name: string, token: string, mode: ClanMode): Promise<Result> {
    return this.post("/clans/mode", token, { name, mode });
  }
  addMember(name: string, token: string, target: string): Promise<Result> {
    return this.post("/clans/add", token, { name, target });
  }
  leave(name: string, token: string): Promise<Result> {
    return this.post("/clans/leave", token, { name });
  }
  kick(name: string, token: string, target: string): Promise<Result> {
    return this.post("/clans/kick", token, { name, target });
  }
  disband(name: string, token: string): Promise<Result> {
    return this.post("/clans/disband", token, { name });
  }

  private async post(path: string, token: string, body: Record<string, unknown>): Promise<Result> {
    try {
      const res = await fetch(`${API_BASE}${path}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      if (!res.ok || !data.ok) return { ok: false, error: data.error ?? "error" };
      return { ok: true };
    } catch {
      return { ok: false, error: "network" };
    }
  }
}
